import { useState, useRef, useEffect } from 'react';
import { getSettings, upsertSetting, getPassLevels, savePassLevels, syncMasterSchedule } from '../../utils/api';

const TEMPLATES = [
  {
    id: 'schedule-link',
    name: 'Schedule Link',
    meta: 'Sent when link is shared',
    trigger: 'Triggered when: Team lead sends a servant leader their schedule link',
    defaultSubject: 'Your Belonging Conference schedule is ready, {{first_name}}',
    defaultBody: `Hi {{first_name}},

Your personal schedule for the Belonging Conference {{year}} is ready. You can view it anytime using the link below — no login needed, and it'll always stay up to date.

{{schedule_link_button}}

You're serving on {{team_name}} this year. If anything changes with your schedule, you'll receive an update automatically.

We're so grateful you're a part of this — can't wait to serve alongside you.

— The Belonging Co Team`,
  },
  {
    id: 'schedule-assignment',
    name: 'Schedule Assignment Request',
    meta: 'Sent when assigned to a block',
    trigger: 'Triggered when: A team lead assigns someone to a block and sends the request',
    defaultSubject: "You've been scheduled for {{block_name}}, {{first_name}}",
    defaultBody: `Hi {{first_name}},

{{team_lead_name}} has scheduled you for a block during the Belonging Conference {{year}}. Please review and confirm your availability.

{{schedule_link_button}}

Thanks so much for serving!

— The Belonging Co Team`,
  },
  {
    id: 'availability-request',
    name: 'Availability Request',
    meta: 'Sent to gather serving availability',
    trigger: 'Triggered when: A team lead clicks "Request Availability" to ask who can serve when',
    defaultSubject: 'When can you serve at the Belonging Conference, {{first_name}}?',
    defaultBody: `Hi {{first_name}},

We're building the serving schedule for the Belonging Conference {{year}} and we'd love to know when you're available. It only takes a minute — just mark the days and times you can serve.

{{availability_button}}

Thank you for serving with us!

— The Belonging Co Team`,
  },
  {
    id: 'schedule-update',
    name: 'Schedule Update',
    meta: 'Sent when schedule changes',
    trigger: 'Triggered when: An admin or team lead modifies a published schedule item',
    defaultSubject: 'Your schedule has been updated, {{first_name}}',
    defaultBody: `Hi {{first_name}},

Your schedule for the Belonging Conference {{year}} has been updated. View the latest version using your schedule link.

{{schedule_link_button}}

If you have questions, reach out to your team lead.

— The Belonging Co Team`,
  },
  {
    id: 'roster-confirmation',
    name: 'Roster Confirmation',
    meta: 'Sent when added to a team',
    trigger: 'Triggered when: Someone is added to a team roster',
    defaultSubject: "You've been added to {{team_name}}, {{first_name}}",
    defaultBody: `Hi {{first_name}},

You've been added to the {{team_name}} for the Belonging Conference {{year}}. We're so excited to have you serving with us!

Your schedule will be shared with you by your team lead closer to the conference.

— The Belonging Co Team`,
  },
  {
    id: 'conference-reminder',
    name: 'Conference Reminder',
    meta: 'Sent X days before conference',
    trigger: 'Triggered when: Automatic reminder fires {{days_until}} days before conference start',
    defaultSubject: "Conference is {{days_until}} days away — here's what you need to know",
    defaultBody: `Hi {{first_name}},

The Belonging Conference is just {{days_until}} days away! Here's a quick reminder of what to expect.

Conference Dates: {{conference_dates}}

View your schedule anytime using your personal link:

{{schedule_link_button}}

We can't wait to serve alongside you.

— The Belonging Co Team`,
  },
  {
    id: 'day-of-reminder',
    name: 'Day-Of Reminder',
    meta: 'Morning of each conference day',
    trigger: 'Triggered when: Automatic morning reminder on each conference day',
    defaultSubject: "Today's your day to serve, {{first_name}}",
    defaultBody: `Hi {{first_name}},

Today's the day! Here's a quick look at your schedule for today.

{{schedule_link_button}}

Thank you for everything you do. See you out there!

— The Belonging Co Team`,
  },
  {
    id: 'multi-team',
    name: 'Multi-Team Addition',
    meta: 'Sent when added to a 2nd team',
    trigger: 'Triggered when: Someone is added to a second (or additional) team',
    defaultSubject: "You've been added to another team, {{first_name}}",
    defaultBody: `Hi {{first_name}},

Great news — you've also been added to {{team_name}} for the Belonging Conference {{year}}. Your schedule link will reflect all your team assignments.

{{schedule_link_button}}

Thanks for going the extra mile!

— The Belonging Co Team`,
  },
  {
    id: 'team-lead-invite',
    name: 'Team Lead Invite',
    meta: 'Sent to new team leads',
    trigger: 'Triggered when: An admin sends a team lead invite from the Team Lead Access page',
    defaultSubject: "You've been invited to manage {{team_name}}, {{first_name}}",
    defaultBody: `Hi {{first_name}},

You've been set up as a Team Lead for {{team_name}} at the Belonging Conference {{year}}. Use the link below to sign in and start building your team schedule.

{{schedule_link_button}}

If you have any questions, reach out to the conference team.

— The Belonging Co Team`,
  },
];

const TOKENS = [
  '{{first_name}}', '{{last_name}}', '{{team_name}}', '{{schedule_link}}',
  '{{schedule_link_button}}', '{{availability_button}}', '{{year}}', '{{conference_dates}}',
  '{{team_lead_name}}', '{{block_name}}', '{{days_until}}',
];

function escapeHtml(str) {
  return (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function buildPreviewHtml(subject, body) {
  const escapedBody = escapeHtml(body).replace(/\n/g, '<br>');
  return `
    <div style="background:#fff;border-radius:8px;padding:28px 32px;max-width:560px;margin:0 auto;font-family:Lato,sans-serif;">
      <div style="font-family:Archivo,sans-serif;font-size:11px;font-weight:700;letter-spacing:0.18em;text-transform:uppercase;color:#888;margin-bottom:6px;">Subject</div>
      <div style="font-family:Archivo,sans-serif;font-size:16px;font-weight:800;color:#202020;margin-bottom:20px;border-bottom:1px solid #E0DED9;padding-bottom:16px;">${escapeHtml(subject)}</div>
      <div style="font-size:14px;font-weight:300;color:#202020;line-height:1.75;">${escapedBody}</div>
    </div>
  `;
}

export default function AdminSettings({ showToast }) {
  const [activeTab, setActiveTab] = useState('email');
  const [selectedTpl, setSelectedTpl] = useState(TEMPLATES[0].id);
  const [drafts, setDrafts] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  // Conference Info state
  const [scriptViewerUrl, setScriptViewerUrl] = useState('');
  const [savingConf, setSavingConf] = useState(false);
  // Master schedule "bible" source — the app mirrors this spreadsheet
  const [masterUrl, setMasterUrl] = useState('');
  const [masterTab, setMasterTab] = useState('');
  const [syncingMaster, setSyncingMaster] = useState(false);

  // Pass Levels state
  const [passLevels, setPassLevels] = useState([]);
  const [newPassLevel, setNewPassLevel] = useState('');
  const [savingPassLevels, setSavingPassLevels] = useState(false);

  // Load settings (scriptViewerUrl) and pass levels on mount
  useEffect(() => {
    getSettings()
      .then(s => {
        if (s?.scriptViewerUrl) setScriptViewerUrl(s.scriptViewerUrl);
        setMasterUrl(s?.masterScheduleUrl || '');
        setMasterTab(s?.masterScheduleTab || '');
      })
      .catch(() => {});
    getPassLevels()
      .then(d => { if (Array.isArray(d)) setPassLevels(d); })
      .catch(() => {});
  }, []);

  function addPassLevel() {
    const trimmed = newPassLevel.trim();
    if (!trimmed || passLevels.includes(trimmed)) return;
    setPassLevels(prev => [...prev, trimmed]);
    setNewPassLevel('');
  }

  function removePassLevel(level) {
    setPassLevels(prev => prev.filter(l => l !== level));
  }

  async function handleSaveConf() {
    setSavingConf(true);
    try {
      await upsertSetting('scriptViewerUrl', scriptViewerUrl.trim());
      await upsertSetting('masterScheduleUrl', masterUrl.trim());
      await upsertSetting('masterScheduleTab', masterTab.trim());
      showToast('Conference info saved.');
    } catch (err) {
      showToast('Error saving — please try again.');
      console.error(err);
    } finally {
      setSavingConf(false);
    }
  }

  async function handleSyncMaster() {
    setSyncingMaster(true);
    try {
      const res = await syncMasterSchedule();
      showToast(res?.report || 'Master schedule synced.');
    } catch (err) {
      console.error('Sync failed:', err);
      showToast('Sync failed — check the link and that the conference account has access.');
    } finally {
      setSyncingMaster(false);
    }
  }

  async function handleSavePassLevels() {
    setSavingPassLevels(true);
    try {
      await savePassLevels(passLevels);
      showToast('Pass levels saved.');
    } catch (err) {
      showToast('Error saving pass levels.');
      console.error(err);
    } finally {
      setSavingPassLevels(false);
    }
  }

  // Load saved templates from backend on mount
  useEffect(() => {
    getSettings()
      .then(settings => {
        if (!settings) return;
        const loaded = {};
        TEMPLATES.forEach(tpl => {
          const subKey = `template.${tpl.id}.subject`;
          const bodyKey = `template.${tpl.id}.body`;
          if (settings[subKey] || settings[bodyKey]) {
            loaded[tpl.id] = {
              subject: settings[subKey] || tpl.defaultSubject,
              body:    settings[bodyKey] || tpl.defaultBody,
            };
          }
        });
        if (Object.keys(loaded).length > 0) setDrafts(loaded);
      })
      .catch(() => {}); // silently fail if settings not yet configured
  }, []);

  const tpl = TEMPLATES.find(t => t.id === selectedTpl);
  const subject = drafts[selectedTpl]?.subject ?? tpl.defaultSubject;
  const body = drafts[selectedTpl]?.body ?? tpl.defaultBody;

  function setSubject(val) {
    setDrafts(d => ({ ...d, [selectedTpl]: { ...d[selectedTpl], subject: val, body: drafts[selectedTpl]?.body ?? tpl.defaultBody } }));
  }

  function setBody(val) {
    setDrafts(d => ({ ...d, [selectedTpl]: { ...d[selectedTpl], subject: drafts[selectedTpl]?.subject ?? tpl.defaultSubject, body: val } }));
  }

  function insertToken(token) {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newVal = body.slice(0, start) + token + body.slice(end);
    setBody(newVal);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await Promise.all([
        upsertSetting(`template.${selectedTpl}.subject`, subject),
        upsertSetting(`template.${selectedTpl}.body`, body),
      ]);
      showToast('Template saved.');
    } catch (err) {
      showToast('Error saving template.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {/* Tab bar */}
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '0 40px', display: 'flex', gap: 0 }}>
        <button className={`settings-tab${activeTab === 'email' ? ' active' : ''}`} onClick={() => setActiveTab('email')}>Email Templates</button>
        <button className={`settings-tab${activeTab === 'conference' ? ' active' : ''}`} onClick={() => setActiveTab('conference')}>Conference Info</button>
        <button className={`settings-tab${activeTab === 'sender' ? ' active' : ''}`} onClick={() => setActiveTab('sender')}>Sender Settings</button>
        <button className={`settings-tab${activeTab === 'passlevels' ? ' active' : ''}`} onClick={() => setActiveTab('passlevels')}>Pass Levels</button>
      </div>

      {/* EMAIL TEMPLATES */}
      {activeTab === 'email' && (
        <div className="page-content">
          <div className="page-title-row">
            <div>
              <div className="page-title">Email Templates</div>
              <div className="page-sub">Customize the emails servant leaders receive from your system</div>
            </div>
          </div>

          <div className="settings-tpl-grid" style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24, alignItems: 'start' }}>
            {/* Left: template list */}
            <div>
              <div className="section-header" style={{ marginBottom: 10 }}>All Templates</div>
              <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
                {TEMPLATES.map(t => (
                  <div
                    key={t.id}
                    className={`email-tpl-item${selectedTpl === t.id ? ' active' : ''}`}
                    onClick={() => setSelectedTpl(t.id)}
                  >
                    <div className="email-tpl-name">{t.name}</div>
                    <div className="email-tpl-meta">{t.meta}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: editor */}
            <div>
              <div className="panel" style={{ padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                  <div>
                    <div style={{ fontFamily: 'var(--font-h)', fontSize: 17, fontWeight: 800, color: 'var(--black)' }}>{tpl.name}</div>
                    <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)', marginTop: 3 }}>{tpl.trigger}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-sm" onClick={() => setPreviewOpen(true)}>Preview Email</button>
                    <button className="btn btn-sm btn-dark" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">Subject Line</label>
                  <input
                    className="form-input"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">Body</label>
                  <textarea
                    ref={bodyRef}
                    className="form-input"
                    rows={12}
                    value={body}
                    onChange={e => setBody(e.target.value)}
                    style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, lineHeight: 1.7, resize: 'vertical' }}
                  />
                </div>

                <div>
                  <div style={{ fontFamily: 'var(--font-h)', fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>
                    Available Variables
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {TOKENS.map(token => (
                      <span key={token} className="tpl-token" onClick={() => insertToken(token)}>{token}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CONFERENCE INFO */}
      {activeTab === 'conference' && (
        <div className="page-content">
          <div className="page-title-row">
            <div>
              <div className="page-title">Conference Info</div>
              <div className="page-sub">Used in email templates and servant leader schedules</div>
            </div>
          </div>
          <div className="panel" style={{ maxWidth: 600, padding: 28 }}>
            <div className="form-group">
              <label className="form-label">Conference Name</label>
              <input className="form-input" defaultValue="Belonging Conference 2026" />
            </div>
            <div className="form-group">
              <label className="form-label">Setup / Arrival Dates</label>
              <input className="form-input" defaultValue="September 12, 2026" />
            </div>
            <div className="form-group">
              <label className="form-label">Conference Dates</label>
              <input className="form-input" defaultValue="September 12–20, 2026" />
            </div>
            <div className="form-group">
              <label className="form-label">Location / Venue</label>
              <input className="form-input" defaultValue="" placeholder="e.g. Bridgestone Arena, Nashville, TN" />
            </div>
            <div className="form-group">
              <label className="form-label">Conference reminder — send this many days before</label>
              <input className="form-input" type="number" defaultValue={7} style={{ maxWidth: 100 }} />
            </div>
            <div style={{ marginTop: 4, fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginBottom: 20 }}>
              Reminder email will send automatically 7 days before the conference start date.
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20, marginBottom: 20 }}>
              <label className="form-label">Master Schedule Spreadsheet</label>
              <div style={{ margin: '4px 0 10px', fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', lineHeight: 1.5 }}>
                The Master Schedule in the app mirrors this Google Sheet — edit times/transport there, and it auto-syncs hourly. For a new year: duplicate the sheet, give <strong>tbcoconference@thebelonging.co</strong> access, paste the new link here, and Save.
              </div>
              <input
                className="form-input"
                value={masterUrl}
                onChange={e => setMasterUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/…"
                style={{ marginBottom: 10 }}
              />
              <label className="form-label">Tab name</label>
              <input
                className="form-input"
                value={masterTab}
                onChange={e => setMasterTab(e.target.value)}
                placeholder="MASTER CONF SCHEDULE"
              />
              <button className="btn btn-sm" onClick={handleSyncMaster} disabled={syncingMaster} style={{ marginTop: 12 }}>
                {syncingMaster ? 'Syncing…' : '↻ Sync from Spreadsheet now'}
              </button>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20, marginBottom: 20 }}>
              <label className="form-label">Script Viewer URL</label>
              <input
                className="form-input"
                value={scriptViewerUrl}
                onChange={e => setScriptViewerUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/…"
              />
              <div style={{ marginTop: 6, fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)' }}>
                When set, a "Script Viewer" link appears at the bottom of the team lead sidebar.
              </div>
            </div>
            <button className="btn btn-dark" onClick={handleSaveConf} disabled={savingConf}>
              {savingConf ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* SENDER SETTINGS */}
      {activeTab === 'sender' && (
        <div className="page-content">
          <div className="page-title-row">
            <div>
              <div className="page-title">Sender Settings</div>
              <div className="page-sub">Control how emails appear when servant leaders receive them</div>
            </div>
          </div>
          <div className="panel" style={{ maxWidth: 600, padding: 28 }}>
            <div className="form-group">
              <label className="form-label">From Name</label>
              <input className="form-input" defaultValue="The Belonging Co" />
            </div>
            <div className="form-group">
              <label className="form-label">From Email Address</label>
              <input className="form-input" defaultValue="conference@thebelonging.co" />
            </div>
            <div style={{ background: 'var(--off)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 20, fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--mid)', lineHeight: 1.6 }}>
              <strong style={{ fontFamily: 'var(--font-h)', fontWeight: 700, color: 'var(--black)' }}>Note:</strong> Schedule link emails are sent from the team lead's own Google account so replies come back to them. All other system emails (reminders, confirmations, updates) send from this address.
            </div>
            <div className="form-group">
              <label className="form-label">Reply-To Address</label>
              <input className="form-input" defaultValue="kat.padilla@thebelonging.co" />
            </div>
            <button className="btn btn-dark" onClick={() => showToast('Sender settings saved.')}>Save</button>
          </div>
        </div>
      )}

      {/* PASS LEVELS */}
      {activeTab === 'passlevels' && (
        <div className="page-content">
          <div className="page-title-row">
            <div>
              <div className="page-title">Pass Levels</div>
              <div className="page-sub">Available options when assigning a pass level to a person or team</div>
            </div>
            <button className="btn btn-dark" onClick={handleSavePassLevels} disabled={savingPassLevels}>
              {savingPassLevels ? 'Saving…' : 'Save Changes'}
            </button>
          </div>

          <div className="panel" style={{ maxWidth: 480, padding: 0, overflow: 'hidden' }}>
            {passLevels.length === 0 && (
              <div style={{ padding: '20px 24px', fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)' }}>
                No pass levels yet — add one below.
              </div>
            )}
            {passLevels.map((level, i) => (
              <div
                key={level}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 20px',
                  borderBottom: i < passLevels.length - 1 ? '1px solid var(--border)' : 'none',
                }}
              >
                <span style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 700, color: 'var(--black)' }}>
                  {level}
                </span>
                <button
                  className="btn btn-xs btn-danger"
                  onClick={() => removePassLevel(level)}
                >
                  Delete
                </button>
              </div>
            ))}
            <div style={{ padding: 16, borderTop: passLevels.length > 0 ? '1px solid var(--border)' : 'none', background: 'var(--off)' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="form-input"
                  placeholder="e.g. VIP Pass, Staff Pass…"
                  value={newPassLevel}
                  onChange={e => setNewPassLevel(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addPassLevel()}
                  style={{ flex: 1, fontSize: 13 }}
                />
                <button className="btn btn-dark" onClick={addPassLevel} disabled={!newPassLevel.trim()}>
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Preview Modal */}
      {previewOpen && (
        <div
          className="modal-overlay"
          onClick={e => { if (e.target === e.currentTarget) setPreviewOpen(false); }}
        >
          <div className="modal" style={{ maxWidth: 660, width: '95vw' }}>
            <div className="modal-header">
              <div className="modal-title">Email Preview — {tpl.name}</div>
              <button className="modal-close" onClick={() => setPreviewOpen(false)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: 0 }}>
              <div
                style={{ background: '#f4f4f4', padding: 24, borderRadius: '0 0 var(--radius) var(--radius)' }}
                dangerouslySetInnerHTML={{ __html: buildPreviewHtml(subject, body) }}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
